import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledCheckbox, ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimContractObjectData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { cnMixSpace } from 'uikit';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { ItemTableContractObject } from '../BimContractStructure/hooks/useBimItemsContractStructureTable';
import { useBimUniqueContractSubObject } from '../BimContractStructure/hooks/useBimUniqueContractSubObject';

import { useBimContractSubObject } from './useBimContractSubObject';

import Styles from './BimCreateStructureSubObject.module.css';

const rules = {
    validate: (value: any) => (!value ? 'Обязательное поле' : true),
};

interface IBimCreateStructureSubObjectProps {
    onClose: () => void;
    isContractTypeNormal: boolean;
    isParentWithGuaranteedVolume: boolean;
    contractObject?: ItemTableContractObject;
    parentContractObject?: ItemTableContractObject | undefined;
    isUseContract?: boolean;
    onSuccess: (item: BimContractObjectData) => void;
}

export const BimCreateStructureSubObject: React.FC<IBimCreateStructureSubObjectProps> = (props) => {
    const {
        contractObject,
        parentContractObject,
        isUseContract,
        onClose,
        onSuccess,
        isContractTypeNormal,
        isParentWithGuaranteedVolume,
    } = props;

    const { defaultValuesContractSubObject, onSubmitContractSubObject } = useBimContractSubObject({
        contractObject,
        parentContractObject,
        isUseContract,
        onClose,
        onSuccess,
    });

    const { control, formState, handleSubmit, watch, setError, clearErrors, reset } = useForm({
        defaultValues: defaultValuesContractSubObject,
    });

    useEffect(() => {
        if (!defaultValuesContractSubObject) {
            reset({
                hasGuaranteedVolume: isContractTypeNormal,
            });
        }
    }, [defaultValuesContractSubObject, isContractTypeNormal, reset]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const objectNameValue = watch('objectName');
    const isDirtyObjectName = formState?.dirtyFields?.objectName;

    const { bimUniqueContractSubObjectData, bimUniqueContractSubObjectStatus, resetBimUniqueContractSubObjectName } =
        useBimUniqueContractSubObject({
            contractObject,
            parentContractObjectId: parentContractObject?.id,
            name: objectNameValue,
            isDirtyObjectName,
        });

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const onSubmitDialog = useCallback(
        (formData) => {
            if (isUseContract) {
                onSubmitContractSubObject(formData);
            }
        },
        [isUseContract, onSubmitContractSubObject],
    );

    useEffect(() => {
        if (
            objectNameValue &&
            bimUniqueContractSubObjectStatus === RequestStatus.SUCCESS &&
            !bimUniqueContractSubObjectData?.result
        ) {
            setError('objectName', { message: 'Подобъект с таким наименованием уже существует' });
        } else {
            clearErrors('objectName');
        }
    }, [
        bimUniqueContractSubObjectData?.result,
        bimUniqueContractSubObjectStatus,
        clearErrors,
        objectNameValue,
        setError,
    ]);

    useEffect(
        () => () => {
            resetBimUniqueContractSubObjectName(null);
        },
        [resetBimUniqueContractSubObjectName],
    );

    const isHasGuaranteedVolumeDisabled = useMemo(() => {
        if (isContractTypeNormal) {
            return true;
        }

        if (contractObject) {
            return contractObject.parent?.hasGuaranteedVolume !== 'Да';
        }

        return !isParentWithGuaranteedVolume;
    }, [contractObject, isParentWithGuaranteedVolume, isContractTypeNormal]);

    const btnConfirmTitle = `${defaultValuesContractSubObject ? 'Редактирование' : 'Создание'} подобъекта`;
    const btnConfirmLabel = defaultValuesContractSubObject ? 'Сохранить' : 'Создать';
    const btnConfirmDisabled = !formState?.isDirty || !R.isEmpty(formState.errors);

    return (
        <ModalDialog
            title={btnConfirmTitle}
            isOpen
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Styles.modal}
        >
            <form className={cnMixSpace({ m: 'xl' })}>
                <ControlledTextField
                    className={cnMixSpace({ mB: 'm' })}
                    control={control}
                    rules={rules}
                    name="objectName"
                    label="Наименование"
                    placeholder="Наименование подобъекта"
                    type="text"
                    view="default"
                    labelPosition="top"
                />
                <ControlledCheckbox
                    className={`${cnMixSpace({ mT: 'xs' })}`}
                    name="hasGuaranteedVolume"
                    label="Признак гарантированного объема"
                    size="l"
                    control={control}
                    disabled={isHasGuaranteedVolumeDisabled}
                    style={{ fontSize: 14 }}
                />
            </form>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

BimCreateStructureSubObject.displayName = 'BimCreateStructureSubObject';
